import { DefaultSeo } from "next-seo";
import Head from "next/head";
import Script from "next/script";
import { useEffect } from "react";

import "@calcom/embed-core/src/embed-iframe";
import { OnboardingPopover } from "@calcom/features/coaches/components/OnboardingCard";
import LicenseRequired from "@calcom/features/ee/common/components/LicenseRequired";
import { trpc } from "@calcom/trpc/react";

import AppProviders, { AppProps } from "@lib/app-providers";
import { seoConfig } from "@lib/config/next-seo.config";

import I18nLanguageHandler from "@components/I18nLanguageHandler";

import "../styles/globals.css";

function MyApp(props: AppProps) {
  const { Component, pageProps, err, router } = props;
  let pageStatus = "200";
  if (router.pathname === "/404") {
    pageStatus = "404";
  } else if (router.pathname === "/500") {
    pageStatus = "500";
  }
  useEffect(() => {
    // @ts-expect-error hsConversationsSettings is dynamically injected by HubSpot script
    window.hsConversationsSettings = {
      loadImmediately: false,
    };
    // @ts-expect-error hsConversationsOnReady is dynamically injected by HubSpot script
    if (!window.hsConversationsOnReady) {
      // @ts-expect-error hsConversationsOnReady is dynamically injected by HubSpot script
      window.hsConversationsOnReady = [];
    }
    // @ts-expect-error hsConversationsOnReady is dynamically injected by HubSpot script
    window.hsConversationsOnReady.push(() => {
      // @ts-expect-error HubSpotConversations is dynamically injected by HubSpot script
      window.HubSpotConversations.on("widgetClosed", () => {
        // @ts-expect-error HubSpotConversations is dynamically injected by HubSpot script
        window.HubSpotConversations.widget.remove();
      });
      // @ts-expect-error HubSpotConversations is dynamically injected by HubSpot script
      window.HubSpotConversations.on("widgetLoaded", () => {
        // @ts-expect-error HubSpotConversations is dynamically injected by HubSpot script
        window.HubSpotConversations.widget.open();
      });
    });
  }, []);

  // On client side don't let nonce creep into DOM
  // It also avoids hydration warning that says that Client has the nonce value but server has "" because browser removes nonce attributes before DOM is built
  // See https://github.com/kentcdodds/nonce-hydration-issues
  // Set "" only if server had it set otherwise keep it undefined because server has to match with client to avoid hydration error
  const nonce = typeof window !== "undefined" ? (pageProps.nonce ? "" : undefined) : pageProps.nonce;
  const providerProps = {
    ...props,
    pageProps: {
      ...props.pageProps,
      nonce,
    },
  };
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  return (
    <AppProviders {...providerProps}>
      <DefaultSeo {...seoConfig.defaultNextSeo} />
      <I18nLanguageHandler />
      <Script
        nonce={nonce}
        id="page-status"
        dangerouslySetInnerHTML={{ __html: `window.CalComPageStatus = '${pageStatus}'` }}
      />
      <Script id="hs-script-loader" strategy="lazyOnload" src="https://js.hs-scripts.com/139600418.js" />

      {/* <!-- Google Tag Manager --> */}
      <Script
        id="google-tag-manager"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-NPK2F6F');`,
        }}
      />
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-NPK2F6F"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>
      {getLayout(
        Component.requiresLicense ? (
          <LicenseRequired>
            <Component {...pageProps} err={err} />
          </LicenseRequired>
        ) : (
          <Component {...pageProps} err={err} />
        ),
        router
      )}
      <OnboardingPopover />
    </AppProviders>
  );
}

export default trpc.withTRPC(MyApp);
